import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { Instrument } from '@prlw/core/monitor/instrument.entity';
import { MainTableInstrumentsService } from '@prlw/core/monitor/main-table-instruments.service';

export type DeliveryAndShipmentTimes = {
  basisCode: string;
  deliveryTime: number | null;
  shipmentTime: number | null;
};

export const EMPTY_DELIVERY_AND_SHIPMENT_TIMES = {
  basisCode: '',
  shipmentTime: null,
  deliveryTime: null,
};

export type SortMode = Exclude<
  keyof DeliveryAndShipmentTimes,
  'basisCode'
> | null;

function mapInstrumentToDeliveryAndShipmentTimes(
  instruments: Instrument[] | null,
): DeliveryAndShipmentTimes[] {
  return (
    instruments?.map(
      (instrument): DeliveryAndShipmentTimes => ({
        basisCode: instrument.shipment.code,
        deliveryTime: instrument.deliveryDays,
        shipmentTime: instrument.shipmentDays,
      }),
    ) ?? []
  );
}

function sortTimes(
  sortMode: SortMode,
  times: DeliveryAndShipmentTimes[],
): DeliveryAndShipmentTimes[] {
  if (!sortMode) {
    return times;
  }
  return times.sort(
    (a, b) => (a[sortMode] as number) - (b[sortMode] as number),
  );
}

@Injectable({
  providedIn: 'root',
})
export class DeliveryTimeGraphService {
  public readonly chartData$: Observable<DeliveryAndShipmentTimes[]>;

  public readonly sortMode$: Observable<SortMode>;
  private readonly sortModeSubject$ = new BehaviorSubject<SortMode>(null);

  constructor(
    private readonly instrumentsService: MainTableInstrumentsService,
  ) {
    this.sortMode$ = this.sortModeSubject$.asObservable();

    this.chartData$ = this.sortMode$.pipe(
      withLatestFrom(this.instrumentsService.instruments$),
      map(([sortMode, instruments]) =>
        sortTimes(
          sortMode,
          mapInstrumentToDeliveryAndShipmentTimes(instruments),
        ),
      ),
    );
  }

  public setSortMode(mode: SortMode): void {
    this.sortModeSubject$.next(mode);
  }
}
